import { useEffect, useState } from 'react';
import get from 'lodash/get';
import { FFFStorage, useUserContext } from '@fff-web/fff-utilities';
import { countryNameCodes } from '../constants/address';
import { Plans } from '../pages/constants';
import { getTaxRate } from '../services/tax';
import useUserShippingAddress from './useUserShippingAddress';

const usePriceCalculator = (plan, subscriptionCost, discount) => {
  const [tax, setTax] = useState({ rate: 0, country: '', zipCode: '' });
  const { userInfo } = useUserContext();
  const shippingAddress = useUserShippingAddress();
  /*
    For US: shipping is free
    For Canada: Shipping is $8 per box.
        Seasonal reactivations: $8 Annual reactivations: $32
    For Alaska, Hawaii, Puerto Rico (US): Shipping is $8 per box.
        Seasonal reactivations: $8 Annual reactivations: $32
    For United Kingdom: Shipping is $10 USD per box.
        Seasonal reactivations: $10  Annual reactivations: $40
   */
  const getShippingCost = (shipCountry, shipState) => {
    if (shipCountry === 'GB') {
      return plan === Plans.ANNUAL ? 40 : 10;
    }
    if (
      shipCountry === 'CA' ||
      (shipCountry === 'US' && (shipState === 'AK' || shipState === 'HI' || shipState === 'PR'))
    ) {
      return plan === Plans.ANNUAL ? 32 : 8;
    }

    return 0;
  };

  const getTaxKey = (shipCountry, shipZipCode) => `tax_${plan}_${shipCountry}_${shipZipCode}`;

  const taxRequest = (shipCountry, shipZipCode) => {
    const taxKey = getTaxKey(shipCountry, shipZipCode);
    const taxFromCache = FFFStorage.getFromStorage({ key: taxKey });
    if (taxFromCache) {
      setTax({
        rate: taxFromCache.tax_rate,
        country: shipCountry,
        zipCode: shipZipCode,
      });
    } else {
      getTaxRate(plan, shipCountry, shipZipCode)
        .then((taxResponse) => {
          if (taxResponse?.tax_rate) {
            setTax({
              rate: taxResponse.tax_rate,
              country: shipCountry,
              zipCode: shipZipCode,
            });
            FFFStorage.setToStorage({
              key: taxKey,
              value: taxResponse,
              durationInMs: 10 * 60 * 1000,
            });
          } else {
            setTax({ rate: 0, country: '', zipCode: '' });
          }
        })
        .catch((error) => {
          console.error(error);
          setTax({ rate: 0, country: '', zipCode: '' });
        });
    }
  };

  useEffect(() => {
    if (
      userInfo &&
      userInfo.user &&
      userInfo.user.shippingCountry &&
      userInfo.user.shippingZipCode
    ) {
      taxRequest(userInfo.user.shippingCountry, userInfo.user.shippingZipCode);
    }
  }, [userInfo, userInfo.user, userInfo.user.shippingCountry, userInfo.user.shippingZipCode]);

  useEffect(() => {
    if (shippingAddress) {
      const countryCode = get(countryNameCodes, get(shippingAddress, 'country'));
      const zip = get(shippingAddress, 'code');
      if ((countryCode && countryCode !== tax.country) || (zip && zip !== tax.zipCode)) {
        taxRequest(countryCode, zip);
      }
    }
  }, [shippingAddress]);

  let shipping = getShippingCost(userInfo.user.shippingCountry, userInfo.user.shippingState);

  if (shippingAddress) {
    const countryCode = get(countryNameCodes, get(shippingAddress, 'country'));
    shipping = getShippingCost(countryCode, shippingAddress.region);
  }

  const totalWithoutTaxes = subscriptionCost - discount + shipping;
  const adjustedTotalWithoutTaxes = Math.max(totalWithoutTaxes, 0);
  const taxAmount = Math.round(adjustedTotalWithoutTaxes * tax.rate * 100) / 100;
  const total = Math.round((adjustedTotalWithoutTaxes + taxAmount) * 100) / 100;

  return {
    tax: taxAmount,
    shipping,
    total,
  };
};

export default usePriceCalculator;
